import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export const TEMPLATES = {
  bingo: { label: "Tombala", desc: "Tahtada ipucu çekilir, öğrenci 3x3 kartında işaretler." },
  classification: { label: "Sınıflandırma", desc: "Takım öğeleri telefondan gruplara yerleştirir." },
  board_classification: { label: "Tahtada Sınıflandırma", desc: "Takımlar sırayla tahtaya gelip özellikleri Benzerlik / Farklılık / Ortak gibi başlıklara koyar." },
  matching: { label: "Eşleştirme / Kart", desc: "Takım kartları eşleriyle eşleştirir." },
  written: { label: "Yazılı Yanıt", desc: "Öğrenci cevabı yazar, otomatik kontrol edilir." },
};

const rows = (n, f) => Array.from({ length: n }, f);
export const blankData = (t) => ({
  bingo: { items: rows(9, () => ({ answer: "", clue: "" })) },
  classification: { categories: [{ name: "" }, { name: "" }], items: rows(4, () => ({ text: "", category: "" })), time_limit: 180 },
  board_classification: { categories: [{ name: "Benzerlik" }, { name: "Farklılık" }, { name: "Ortak" }], items: rows(4, () => ({ text: "", category: "", points: 10 })), time_limit: 300 },
  matching: { pairs: rows(3, () => ({ left: "", right: "" })), time_limit: 180 },
  written: { prompts: [{ text: "", answers: "" }], points: 10, time_limit: 30 },
})[t];

export const toForm = (a) => {
  const d = a.data;
  if (a.template === "bingo") return { items: d.items.map(({ answer, clue }) => ({ answer, clue })) };
  if (a.template === "matching") return { pairs: d.pairs, time_limit: d.time_limit };
  if (a.template === "written") return { prompts: d.prompts.map((p) => ({ text: p.text, answers: p.answers.join(", ") })), points: d.points, time_limit: d.time_limit };
  const name = Object.fromEntries(d.targets.map((t) => [t.id, t.text]));
  return { categories: d.targets.map((t) => ({ name: t.text })), items: d.items.map((i) => ({ text: i.text, category: name[i.target], ...(i.points != null ? { points: i.points } : a.template === "board_classification" ? { points: 10 } : {}) })), time_limit: d.time_limit };
};

export const toApi = (t, d) => (t === "written"
  ? { ...d, prompts: d.prompts.map((p) => ({ text: p.text, answers: p.answers.split(",").map((s) => s.trim()).filter(Boolean) })) }
  : d);

const Rows = ({ list, onChange, cols, min, max, addLabel, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    {list.map((r, i) => (
      <div key={i} className="flex items-center gap-2">
        <span className="w-6 text-right text-sm text-slate-500">{i + 1}</span>
        {cols.map((c) => (c.options ? (
          <select key={c.key} value={r[c.key]} onChange={(e) => onChange(list.map((x, j) => (j === i ? { ...x, [c.key]: e.target.value } : x)))}
            className="h-11 rounded-md border border-[rgba(71,85,105,0.45)] bg-slate-900 px-2 text-sm" data-testid={`${testId}-${c.key}-${i}`}>
            <option value="">Grup seç</option>
            {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <Input key={c.key} value={r[c.key] ?? ""} placeholder={c.placeholder} type={c.number ? "number" : "text"} className={`h-11 ${c.number ? "w-24" : c.wide ? "flex-[2]" : "flex-1"}`}
            onChange={(e) => onChange(list.map((x, j) => (j === i ? { ...x, [c.key]: c.number ? Number(e.target.value) : e.target.value } : x)))} data-testid={`${testId}-${c.key}-${i}`} />
        )))}
        {list.length > min && <Button type="button" variant="ghost" size="icon" onClick={() => onChange(list.filter((_, j) => j !== i))} aria-label="Sil" data-testid={`${testId}-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    ))}
    {list.length < max && (
      <Button type="button" variant="outline" onClick={() => onChange([...list, Object.fromEntries(cols.map((c) => [c.key, c.number ? 10 : ""]))])} data-testid={`${testId}-add`}>
        <Plus className="h-4 w-4 mr-1" />{addLabel}
      </Button>
    )}
  </div>
);

const NumField = ({ label, value, onChange, testId }) => (
  <div className="space-y-1.5 w-40">
    <Label>{label}</Label>
    <Input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className="h-11" data-testid={testId} />
  </div>
);

const Hint = ({ children }) => <p className="text-sm text-slate-400">{children}</p>;

export const ActivityFields = ({ template, data, setData }) => {
  const set = (k) => (v) => setData({ ...data, [k]: v });
  if (template === "bingo") {
    return (
      <div className="space-y-3">
        <Hint>Kartta görünecek cevap ve tahtada çekilecek ipucu (en az 9, en fazla 40). Her öğrenciye bu listeden rastgele 9 cevaplık kart düşer.</Hint>
        <Rows list={data.items} onChange={set("items")} min={9} max={40} addLabel="Satır ekle" testId="bingo-rows"
          cols={[{ key: "answer", placeholder: "Cevap (örn. Fe)" }, { key: "clue", placeholder: "İpucu (örn. Sembolü Fe olan element)", wide: true }]} />
      </div>
    );
  }
  if (template === "classification" || template === "board_classification") {
    const board = template === "board_classification";
    const names = data.categories.map((c) => c.name.trim()).filter(Boolean);
    return (
      <div className="space-y-5">
        <div className="space-y-2"><Hint>{board ? "Tahtadaki başlıklar (2-4) · örn. Benzerlik, Farklılık, Ortak — istediğiniz gibi yeniden adlandırın" : "Gruplar (2-6)"}</Hint>
          <Rows list={data.categories} onChange={set("categories")} min={2} max={board ? 4 : 6} addLabel={board ? "Başlık ekle" : "Grup ekle"} testId="cls-cats" cols={[{ key: "name", placeholder: board ? "Başlık" : "Grup adı" }]} /></div>
        <div className="space-y-2"><Hint>{board ? "Özellikler (4-40) ve doğru başlıkları" : "Öğeler (4-40) ve doğru grupları"}</Hint>
          <Rows list={data.items} onChange={set("items")} min={4} max={40} addLabel={board ? "Özellik ekle" : "Öğe ekle"} testId="cls-items"
            cols={[{ key: "text", placeholder: board ? "Özellik" : "Öğe" }, { key: "category", options: names }, ...(board ? [{ key: "points", placeholder: "Puan", number: true }] : [])]} /></div>
        {!board && <NumField label="Süre (sn)" value={data.time_limit} onChange={set("time_limit")} testId="activity-time" />}
      </div>
    );
  }
  if (template === "matching") {
    return (
      <div className="space-y-3">
        <Hint>Kart çiftleri (3-20). Öğrenci sol kartı alır, tahtadaki sağ kartlar arasından eşini seçer.</Hint>
        <Rows list={data.pairs} onChange={set("pairs")} min={3} max={20} addLabel="Çift ekle" testId="match-rows"
          cols={[{ key: "left", placeholder: "Kart" }, { key: "right", placeholder: "Eşi" }]} />
        <NumField label="Süre (sn)" value={data.time_limit} onChange={set("time_limit")} testId="activity-time" />
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <Hint>Sorular (1-20). Kabul edilen cevapları virgülle ayırın; büyük/küçük harf, Türkçe karakter ve alt simge farkı yok sayılır.</Hint>
      <Rows list={data.prompts} onChange={set("prompts")} min={1} max={20} addLabel="Soru ekle" testId="written-rows"
        cols={[{ key: "text", placeholder: "Soru", wide: true }, { key: "answers", placeholder: "Cevaplar (Altın, Au)" }]} />
      <div className="flex gap-4">
        <NumField label="Puan" value={data.points} onChange={set("points")} testId="activity-points" />
        <NumField label="Soru başı süre (sn)" value={data.time_limit} onChange={set("time_limit")} testId="activity-time" />
      </div>
    </div>
  );
};
